'use client';

import React, { useMemo } from 'react';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import {
  IRelatedNews,
  IResortPageInfo,
  IResortPathInfo,
  ISnowfallItem,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { HistoricalSnowfall } from '@/components/resort/HistoricalSnowfall';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { Hero400Error } from '@/components/400';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { useDistanceFormatter } from '@/i18n/unitFormat';

export type ResortHistoricalSnowfallPageProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  snowfallInfoMonthly: Array<ISnowfallItem> | null,
  snowfallInfoAnnual: Array<ISnowfallItem> | null
  relatedNews: IRelatedNews | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-historical-snowfall-page';
export const TEMPLATE_ID = '3.2.5';
export const TEMPLATE_NAME = 'Resort Historical Snowfall';

export default function ResortHistoricalSnowfallPage({
  resort,
  pathInfo,
  snowfallInfoMonthly,
  snowfallInfoAnnual,
  relatedNews,
}: ResortHistoricalSnowfallPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  type KeyAverage = 'totalSnow' | 'snowDays' | 'baseDepth' | 'maxBaseDepth' | 'biggestSnowfall';
  const setAverageInstance = (property: KeyAverage) => {
    const sum = (snowfallInfoAnnual || []).reduce((prev, item) => prev + item[property], 0);
    return Math.ceil(sum / (snowfallInfoAnnual?.length || 1));
  };

  const averages = useMemo(() => ({
    date: t('resort.historicalSnowfall.average'),
    totalSnow: setAverageInstance('totalSnow'),
    snowDays: setAverageInstance('snowDays'),
    baseDepth: setAverageInstance('baseDepth'),
    maxBaseDepth: setAverageInstance('maxBaseDepth'),
    biggestSnowfall: setAverageInstance('biggestSnowfall'),
  }), [snowfallInfoAnnual]);

  const maxBiggestSnowfall = Math.max(0, ...(snowfallInfoAnnual ?? []).map((v) => v.biggestSnowfall));

  if (!resort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={t('resort.historicalSnowfall.seoTitle', { resort: resort.title })}>
            {m('resort.historicalSnowfall.seoText', {
              resort: resort.title,
              avgsnowfall: dt(averages.totalSnow),
              avgsnowfalldays: `${averages.snowDays ?? ''}`,
              bgstsnowfall: dt(maxBiggestSnowfall),
            })}
          </SeoSection>
          {snowfallInfoAnnual
            && snowfallInfoMonthly
            && (!!(snowfallInfoMonthly.length) || !!(snowfallInfoAnnual.length)) && (
              <HistoricalSnowfall
                snowfallInfoMonthly={snowfallInfoMonthly}
                snowfallInfoAnnual={snowfallInfoAnnual}
                resortName={resort.title}
              />
          )}
          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={resort.title}
          />
          <PlanYourTrip
            hasSkiRental={pathInfo.hasSkiRental}
            hasFlights={pathInfo.hasFlights}
            hasCarRental={pathInfo.hasCarRental}
            resort={pathInfo}
            liftsUrl={resort.liftsUrl}
            rentalUrl={resort.rentalUrl}
            lessonsUrl={resort.lessonsUrl}
            isPartner={resort.isPartner}
            resortCountry={resort.region?.country_code}
          />
          <RegularAd isFirst />
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

// fetchProps moved to ./historical-snowfall.server.ts so the App Router RSC
// page can import the data fetcher without pulling in this 'use client' bundle.
